/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useMutation, useQuery } from "@apollo/client";
import React, { useEffect } from "react";
import css from "classnames";
import { Grid, Typography } from "@material-ui/core";
import { Viewer } from "components/auth/types";
import { SubmitValues, TemplateForm } from "components/send-messages";
import { TemplateSelector } from "components/send-messages/template-selector";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import { calculateMessageValues } from "utils/template";
import SlideoutEnabledContext from "pages/waitlist-update/context";
import { getTemplateGroup_templateGroup as Template,
  Feature,
  SendMessagesProfileVariables,
  SendMessagesProfile,
  SidebarFilters,
  SidebarFiltersVariables } from "types/gql-types";
import { getLanguageNameByCode } from "../utils";
import { isPropertyFeatureEnabled } from "../interests";
import { QUERY_SIDEBAR_FILTERS } from "../interests/interests-sidebar/query";
import ActivityFeed from "./activity-feed";
import ActivityForm from "./activity-form";
import { PhoneSubView } from "./components/subview-phone";
import styles from "./interestsActivities.module.css";
import { SEND_MESSAGE } from "./queries";

// eslint-disable-next-line init-declarations
let logCallNode: HTMLButtonElement;
// eslint-disable-next-line init-declarations
let addNoteNode: HTMLButtonElement;
// eslint-disable-next-line init-declarations
let sendMessageNode: HTMLButtonElement;

const enum SubView {
  Note,
  Message,
  Phone,
}

const ERROR_MSG =
  "Error sending bulk message. Please don't retry in case " +
  "part of the batch has been sent. Contact support@havenconnect.com";
const SUCCESS_MESSAGE = "Success! Messages scheduled to send.";

interface Props {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  interest: { [key: string]: any };
  interestID: string;
  isLoading: boolean;
  isLoadingInterest: boolean;
  isLoadingPdf: boolean;
  interestListID: string;
  viewer: Viewer;

  // used to determine whether to fire a click even on the subview
  // during load
  logCallIndex: number;
  addNoteIndex: number;
  sendMessageIndex: number;

  cleanupNavigateAction: () => void;
}

export const ActivityContent: React.FC<Props> = (props) => {
  const { interest, logCallIndex, addNoteIndex, sendMessageIndex, cleanupNavigateAction } = props;
  const [isSending, setIsSending] = React.useState(false);
  const [selectedView, setSelectedView] = React.useState(SubView.Phone);
  const [template, setTemplate] = React.useState<Template | null>(null);

  const [sendTemplateMessages] =
    useMutation<SendMessagesProfile, SendMessagesProfileVariables>(SEND_MESSAGE);
  const updateValues = calculateMessageValues(template);

  const { openSnackbar } = useSnackbar();
  const query = useQuery<
  SidebarFilters, SidebarFiltersVariables
  >(QUERY_SIDEBAR_FILTERS, { variables: { interestListIds: [props.interestListID] } });

  const interestList = query.data?.interestListsById[0];
  const features = interestList?.property?.features || [];

  const slideoutEnabled = isPropertyFeatureEnabled(features, Feature.INTEREST_PROFILE_SLIDEOUT);


  let firstName = interest.applicant_first_name || interest.household_member_0_first_name;
  let lastName = interest.applicant_last_name || interest.household_member_0_last_name;
  if (!firstName && !lastName) {
    firstName = "N/A";
    lastName = "";
  }

  const [logCallReady, setLogCallReadyState] = React.useState(false);
  const setLogCallReady = (node: HTMLButtonElement) => {
    logCallNode = node;
    setLogCallReadyState(true);
  };
  const [addNoteReady, setAddNoteReadyState] = React.useState(false);
  const setAddNoteReady = (node: HTMLButtonElement) => {
    addNoteNode = node;
    setAddNoteReadyState(true);
  };
  const [sendMessageReady, setSendMessageReadyState] = React.useState(false);
  const setSendMessageReady = (node: HTMLButtonElement) => {
    sendMessageNode = node;
    setSendMessageReadyState(true);
  };

  const genRefCallback = (setReady: (node: HTMLButtonElement) => void) =>
    (node: HTMLButtonElement) => {
      if (node) {
        setReady(node);
      }
    };

  const genEffectCallback =
  (buttonReady: boolean, callIndex: number, buttonNode: HTMLButtonElement) => () => {
    if (buttonReady && callIndex !== -1) {
      buttonNode.click();

      cleanupNavigateAction();
    }
  };

  useEffect(
    genEffectCallback(logCallReady, logCallIndex, logCallNode),
    [logCallReady, logCallIndex],
  );
  useEffect(
    genEffectCallback(addNoteReady, addNoteIndex, addNoteNode),
    [addNoteReady, addNoteIndex],
  );
  useEffect(
    genEffectCallback(sendMessageReady, sendMessageIndex, sendMessageNode),
    [sendMessageReady, sendMessageIndex],
  );

  const subViews = [
    {
      name: SubView.Phone,
      label: "Log a Call",
      routeActionIndex: logCallIndex,
      ref: genRefCallback(setLogCallReady),
    },
    {
      name: SubView.Note,
      label: "Notes",
      routeActionIndex: addNoteIndex,
      ref: genRefCallback(setAddNoteReady),
    },
    {
      name: SubView.Message,
      label: "Message",
      routeActionIndex: sendMessageIndex,
      ref: genRefCallback(setSendMessageReady),
    },
  ];

  const sendMessage = (messageParams: SubmitValues) => {
    setIsSending(true);
    sendTemplateMessages({
      variables: {
        interestListIds: [props.interestListID],
        interestIds: [props.interestID],
        elasticQuery: null,
        ...messageParams,
      },
    })
      .then(() => {
        openSnackbar({ message: SUCCESS_MESSAGE, status: "success" });
        if (template) {
          setTemplate(null);
        }
        setIsSending(false);
        setSelectedView(SubView.Note);
      })
      .catch((err) => {
        setIsSending(false);
        openSnackbar({ message: ERROR_MSG, status: "error" });
        logger.sendError(err);
        throw new Error(err);
      });
  };

  const renderSubView = (view: SubView) => {
    switch (view) {
      case SubView.Note:
        return <ActivityForm onSubmit={props.onSubmit} isLoading={props.isLoading} />;

      case SubView.Phone:
        return <PhoneSubView interestId={props.interestID} />;

      case SubView.Message:
        return (
          <div>
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                To: {`${firstName} ${lastName}`}
                {interest.pref_lang && interest.pref_lang !== "en" && (
                  <span className="badge hc-badge">
                    {getLanguageNameByCode(interest.pref_lang)}
                  </span>
                )}
              </Typography>
            </Grid>
            <TemplateSelector
              onChange={(selectedTemplate: Template | null) => {
                if (selectedTemplate) setTemplate(selectedTemplate);
              }}
              viewer={props.viewer}
              currentTemplate={template}
            />
            <hr/>
            <Grid item xs={12}>
              <TemplateForm
                viewer={props.viewer}
                handleSubmit={sendMessage}
                isSending={isSending}
                template={template}
                currentListId={props.interestListID}
                onRequestClose={() => {
                  if (template) setTemplate(null);
                }}
                {...updateValues}
              />
            </Grid>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="panel panel-default">
        <div className="panel-heading">Send messages / Create note</div>
        <div className="panel-body">
          <div
            className={`btn-group ${styles.buttonBar}`}
            role="group"
            aria-label="Default button group"
          >
            {subViews.map(({ name, label, ref }) => (
              <button
                key={name}
                className={css("btn", {
                  [styles.buttonSecondaryColor]: selectedView === name,
                  "btn-border": selectedView !== name,
                })}
                ref={ref}
                type="button"
                onClick={() => setSelectedView(name)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={styles.messageComponentWrapper}>
            <div>{renderSubView(selectedView)}</div>
          </div>
        </div>
      </div>

      <div className="panel panel-default">
        <div className="panel-heading">View application activity</div>
        <div className="panel-body">
          <SlideoutEnabledContext.Provider value={{ slideoutEnabled }}>
            <ActivityFeed interestId={props.interestID} />
          </SlideoutEnabledContext.Provider>
        </div>
      </div>
    </>
  );
};
